import type { Customer } from '../auth/api'
import { isApiError } from '@dorixona/api'
import type { Translate } from '../../ui/translate'
import { failureText } from '../../ui/Load'
import type { LedgerType, OrderStatus } from './api'

/** Статусы, у которых есть название для покупателя (`account:status.*`). Ключи — из API. */
export const STATUSES: OrderStatus[] = [
  'awaiting_payment',
  'payment_expired',
  'new',
  'needs_attention',
  'assembling',
  'assembled',
  'awaiting_courier',
  'in_delivery',
  'delivered',
  'ready_for_pickup',
  'issued',
  'reservation_expired',
  'cancelled',
]

/** Как называется статус заказа; незнакомый — кодом, а не пустотой. */
export function statusText(status: OrderStatus, t: Translate): string {
  return STATUSES.includes(status) ? t(`account:status.${status}`) : status
}

/** Заказ закрыт — значок нейтральный, а не брендовый. */
export function isOrderClosed(status: OrderStatus): boolean {
  return status === 'delivered' || status === 'issued'
}

/** Статусы курьера от службы доставки (`account:courier.status.*`); пустой — ищем машину. */
export const COURIER_STATUSES = [
  'courier_assigned',
  'at_pickup',
  'picked_up',
  'delivered',
  'cancelled',
] as const

/** Этапы поездки курьера по порядку — для шкалы «где курьер» (`account:track.step.*`). */
export const COURIER_STEPS = [
  'searching',
  'courier_assigned',
  'at_pickup',
  'picked_up',
  'delivered',
] as const

/**
 * Имя водителя так, как его пишут люди: служба доставки присылает его
 * заглавными («БОЗОРОВ БОБУР»), а кричащее имя читается хуже. Имя, где уже
 * есть строчные буквы, оставляем как есть.
 */
export function personName(name: string): string {
  if (name !== name.toUpperCase()) return name
  return name
    .toLowerCase()
    .replace(/(^|[\s-])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase())
}

/** Номер текущего этапа на шкале; −1 — поездку отменили или служба прислала незнакомое слово. */
export function courierStep(status: string): number {
  return (COURIER_STEPS as readonly string[]).indexOf(status || 'searching')
}

export function courierStatusText(status: string, t: Translate): string {
  if (!status) return t('account:courier.status.searching')
  return (COURIER_STATUSES as readonly string[]).includes(status)
    ? t(`account:courier.status.${status}`)
    : status
}

/** Из чего движение баллов (`account:ledger.*`). */
export const LEDGER_TYPES: LedgerType[] = [
  'welcome_bonus',
  'birthday_bonus',
  'order_accrual',
  'offline_accrual',
  'offline_spend',
  'order_spend',
  'spend_release',
]

export function ledgerText(type: LedgerType, t: Translate): string {
  return LEDGER_TYPES.includes(type) ? t(`account:ledger.${type}`) : type
}

/** Значок операции: подарок, онлайн-заказ или касса аптеки. */
export function transactionIcon(type: LedgerType): string {
  if (type === 'welcome_bonus' || type === 'birthday_bonus') return 'i-gift'
  if (type === 'offline_accrual' || type === 'offline_spend') return 'i-store'
  return 'i-box'
}

export function initials(firstName: string, lastName: string): string {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`
}

export interface ProfileView {
  /** Профиль загружен: до этого имени и номера у нас нет. */
  known: boolean
  fullName: string
  initials: string
  phone: string
}

/**
 * Как показать вошедшего, пока профиль ещё в пути. Одно место на все экраны:
 * иначе каждый решал бы про прочерк и пустой номер по-своему.
 */
export function profileView(customer: Customer | null): ProfileView {
  if (!customer) return { known: false, fullName: '—', initials: '', phone: '' }
  return {
    known: true,
    fullName: `${customer.firstName} ${customer.lastName}`,
    initials: initials(customer.firstName, customer.lastName),
    phone: customer.phone,
  }
}

/** Почему аккаунт не удалился: пока есть незавершённый заказ — `orders_open` (R-114). */
export function deleteFailureText(error: unknown, t: Translate): string {
  if (isApiError(error) && error.code === 'orders_open') return t('account:delete.openOrder')
  return failureText(error, t)
}

/** До готовности к выдаче: заказ ещё собирают, бронь начнётся позже. */
const BEFORE_READY: OrderStatus[] = ['new', 'needs_attention', 'assembling']

export type Reservation = { at: number } | { hours: number } | null

/**
 * Что сказать про бронь (R-39, F-06). Бронь есть только у самовывоза с
 * оплатой в аптеке: оплаченный заказ ждёт без срока. Готов к выдаче — до
 * какого времени забрать; ещё собирают — на сколько часов его забронируют.
 */
export function reservationOf(
  order: { ship: string; payMethod: string; status: OrderStatus; reservationExpires: number },
  hours: number,
): Reservation {
  if (order.ship !== 'pickup' || order.payMethod !== 'pay_at_pharmacy') return null
  if (order.status === 'ready_for_pickup' && order.reservationExpires) {
    return { at: order.reservationExpires }
  }
  if (BEFORE_READY.includes(order.status) && hours > 0) return { hours }
  return null
}
